/**
 * Check if a PDF file is password-protected using pdfjs-dist
 *
 * Web version - uses pdfjs-dist to detect password-protected PDFs
 */
import type { ProtectionCheckResult } from './checkFileProtection';

export async function checkPdfProtection(arrayBuffer: ArrayBuffer): Promise<ProtectionCheckResult> {
  try {
    // Dynamic import to avoid bundle impact when not used
    const { getDocument, PasswordResponses } = await import(
      /* webpackChunkName: "pdfjs-dist" */ 'pdfjs-dist'
    );

    return new Promise((resolve) => {
      const loadingTask = getDocument({
        data: arrayBuffer,
        // Disable worker to avoid worker setup complexity in this context
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true,
      });

      loadingTask.onPassword = (_updateCallback: (password: string) => void, reason: number) => {
        // Cancel the loading task since we don't want to actually open it
        loadingTask.destroy();

        if (reason === PasswordResponses.NEED_PASSWORD) {
          resolve({
            isProtected: true,
            fileType: 'pdf',
            error: 'PDF is password-protected',
          });
        } else {
          // INCORRECT_PASSWORD means we tried with wrong password,
          // but for detection purposes, we just care that it needs one
          resolve({
            isProtected: true,
            fileType: 'pdf',
            error: 'PDF is password-protected',
          });
        }
      };

      loadingTask.promise
        .then((pdfDocument) => {
          // Successfully opened without password - not protected
          pdfDocument.destroy();
          resolve({ isProtected: false, fileType: 'pdf' });
        })
        .catch((error: Error) => {
          // Check if error is password-related
          if (error.name === 'PasswordException') {
            resolve({
              isProtected: true,
              fileType: 'pdf',
              error: 'PDF is password-protected',
            });
          } else {
            // Other errors (corrupt file, etc.) - not a protection issue
            resolve({ isProtected: false, fileType: 'pdf' });
          }
        });
    });
  } catch {
    // If we can't load pdfjs, assume not protected to avoid blocking
    return { isProtected: false, fileType: 'pdf' };
  }
}
